import { isRecord, type RecordOf } from 'immutable';

import { LEGACY_RECENT_RANGE_MAP, type LegacyRecentRange } from 'app-components/utils/date';

import {
  DEFAULT_DELTA_DISPLAY_MODE,
  mkReportConfiguration,
  normalizeReportConfigurationDateRanges,
  type ReportConfigurationInputProps,
  type ReportConfigurationProps,
} from './reportConfigurationsTypes';
import { omitConflictingDateRangeFields, resolveDisplayChartType } from './utils/reportFieldAvailability';

const transformDisplayOptionsToRequestData = (
  displayOptions: ReportConfigurationProps['displayOptions'],
): ReportConfigurationProps['displayOptions'] => {
  if (!displayOptions) {
    return displayOptions;
  }

  if (displayOptions.collapseAll !== null && displayOptions.collapseAll !== undefined) {
    return displayOptions;
  }

  // null/undefined mean "no saved expand/collapse preference"; omit the field from QCS requests.
  const { collapseAll: _collapseAll, ...requestDisplayOptions } = displayOptions;
  return requestDisplayOptions;
};

export function transformReportConfigurationToRequestData(
  reportConfiguration: ReportConfigurationInputProps | RecordOf<ReportConfigurationProps>,
): ReportConfigurationProps {
  if (isRecord(reportConfiguration)) {
    const requestData = normalizeReportConfigurationDateRanges(
      reportConfiguration.toJS() as ReportConfigurationInputProps,
    );
    return omitConflictingDateRangeFields({
      ...requestData,
      displayOptions: transformDisplayOptionsToRequestData(requestData.displayOptions),
    });
  }

  const requestData = normalizeReportConfigurationDateRanges({
    id: reportConfiguration.id,
    clientId: reportConfiguration.clientId,

    name: reportConfiguration.name,
    description: reportConfiguration.description,
    source:
      reportConfiguration.source ?? (reportConfiguration.name === 'net-worth-page' ? 'NET_WORTH' : 'SAVED_REPORTS'),

    accountingType: reportConfiguration.accountingType,
    deductionType: reportConfiguration.deductionType,
    reportType: reportConfiguration.reportType,
    reportSubType: reportConfiguration.reportSubType,
    persistentType: reportConfiguration.persistentType,

    dateRange: reportConfiguration.dateRange,
    startOn: reportConfiguration.startOn,
    endOn: reportConfiguration.endOn,

    dateRangeInterval: reportConfiguration.dateRangeInterval,
    dateRangeIntervalCount: reportConfiguration.dateRangeIntervalCount,

    columnType: reportConfiguration.columnType,
    rowType: reportConfiguration.rowType,
    timestep: reportConfiguration.timestep,

    comparisonRanges: reportConfiguration.comparisonRanges,
    primaryAdditionalColumnTypes: reportConfiguration.primaryAdditionalColumnTypes,

    showTotalRows: reportConfiguration.showTotalRows,

    filterId: reportConfiguration.filterId,
    filter: reportConfiguration.filter,

    displayOptions: transformDisplayOptionsToRequestData(reportConfiguration.displayOptions),
    chartType: reportConfiguration.chartType,

    predefinedConfigId: reportConfiguration.predefinedConfigId,
    parentConfigId: reportConfiguration.parentConfigId,
    parentPersistentType: reportConfiguration.parentPersistentType,
    isParentDeleted: reportConfiguration.isParentDeleted,

    isDeleted: reportConfiguration.isDeleted,
  });

  return omitConflictingDateRangeFields(requestData);
}

const toLegacyRecentRange = <T extends string>(range: T): T | LegacyRecentRange =>
  (Object.keys(LEGACY_RECENT_RANGE_MAP) as LegacyRecentRange[]).find(
    (legacyRange) => LEGACY_RECENT_RANGE_MAP[legacyRange] === range,
  ) ?? range;

/** Keep saved configurations readable by older clients while using RECENT_* in app state. */
export function transformReportConfigurationToPersistenceData(
  reportConfiguration: ReportConfigurationInputProps | RecordOf<ReportConfigurationProps>,
): ReportConfigurationInputProps {
  const requestData = transformReportConfigurationToRequestData(reportConfiguration);
  return {
    ...requestData,
    chartType: resolveDisplayChartType(
      requestData.reportType,
      requestData.reportSubType,
      requestData.chartType,
      requestData.timestep,
    ),
    dateRange: toLegacyRecentRange(requestData.dateRange),
    comparisonRanges: requestData.comparisonRanges?.map((range) => ({
      ...range,
      dateRange: toLegacyRecentRange(range.dateRange),
    })),
  };
}

export const transformResponseToReportConfigurations = (
  response?: { data?: { resources?: ReportConfigurationInputProps[] } } | null,
): RecordOf<ReportConfigurationProps>[] | null | undefined => {
  if (!response || !response.data || !response.data.resources) {
    return null;
  }

  const reportConfig = response?.data?.resources?.map((data) =>
    mkReportConfiguration({
      id: data.id,
      clientId: data.clientId,

      name: data.name,
      description: data.description,
      source: data.source ?? (data.name === 'net-worth-page' ? 'NET_WORTH' : 'SAVED_REPORTS'),

      accountingType: data.accountingType,
      deductionType: data.deductionType,
      reportType: data.reportType,
      reportSubType: data.reportSubType,
      persistentType: data.persistentType,

      dateRange: data.dateRange,
      startOn: data.startOn,
      endOn: data.endOn,

      dateRangeInterval: data.dateRangeInterval,
      dateRangeIntervalCount: data.dateRangeIntervalCount,

      columnType: data.columnType,
      rowType: data.rowType,
      // QCS omits `timestep` from responses for non-time-series reports (TRANSACTION),
      // but Redux/UI code treats `timestep` as a required enum: `'MONTH'` for SUMMARY,
      // `'NONE'` otherwise (see `adjustReportTypeIfInvalid` in utils/reportFieldAvailability.ts
      // and the predefined config defaults). Backfill the canonical value so `isReportConfigSynced`
      // in TotalSpending doesn't pin the spinner on after Save/Undo.
      // COMPARISON intentionally defaults to `'NONE'`: users opt into a timestep from the
      // comparison interval control, while an explicit server value is preserved.
      timestep: data.timestep ?? (data.reportType === 'SUMMARY' ? 'MONTH' : 'NONE'),

      comparisonRanges: data.comparisonRanges,
      primaryAdditionalColumnTypes: data.primaryAdditionalColumnTypes,

      filter: data.filter,
      filterId: data.filterId,

      displayOptions: data.displayOptions ?? {
        alternateRows: false,
        deltaDisplayMode: DEFAULT_DELTA_DISPLAY_MODE,
        density: 'STANDARD',
        hideCents: false,
        showExpensesAsNegative: false,
        showRowNumbers: false,
        showOtherOnTop: false,
        showSubTotalsOnTop: false,
        showExcludedTxns: false,
      },
      chartType: data.chartType,

      predefinedConfigId: data.predefinedConfigId,
      parentConfigId: data.parentConfigId,
      parentPersistentType: data.parentPersistentType,
      isParentDeleted: data.isParentDeleted,

      order: data.order,
      isHidden: data.isHidden,

      isDeleted: data.isDeleted,
      modifiedAt: data.modifiedAt,
      dbVersion: data.dbVersion,
    }),
  );

  return reportConfig || [];
};
